import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { usePlayfabAuthContext, ArenaLayoutSummary } from '../contexts/PlayfabAuthContext';
import { LoginForm } from '../components/LoginForm';

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export const MyLayouts: React.FC = () => {
  const playfab = usePlayfabAuthContext();
  const history = useHistory();

  const [busy, setBusy] = useState(false);
  const [layouts, setLayouts] = useState<ArenaLayoutSummary[]>();
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (!playfab.isLoggedIn || layouts !== undefined) return;
    setBusy(true);
    playfab.api.listMyArenaLayouts().then((result) => {
      setBusy(false);
      if (!result.success) {
        alert(`Could not load your layouts.\n\n${result.error}`);
        return;
      }
      setLayouts(result.layouts ?? []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playfab.isLoggedIn, layouts]);

  if (!playfab.isLoggedIn) {
    return <LoginForm />;
  }

  const handleEdit = (layout: ArenaLayoutSummary) => {
    history.push({
      pathname: '/field-editor',
      search: `?editItemId=${encodeURIComponent(layout.itemId)}`,
      state: {
        itemId: layout.itemId,
        friendlyId: layout.friendlyId,
        name: layout.name,
        contentUrl: layout.contentUrl,
      },
    });
  };

  const handleDelete = async (layout: ArenaLayoutSummary) => {
    if (!window.confirm(`Delete "${layout.name}"? This cannot be undone.`)) {
      return;
    }
    setDeletingId(layout.itemId);
    const result = await playfab.api.deleteArenaLayout(layout.itemId);
    setDeletingId(null);
    if (!result.success) {
      alert(`Could not delete this layout.\n\n${result.error}`);
      return;
    }
    setLayouts((prev) => prev?.filter((l) => l.itemId !== layout.itemId));
  };

  return (
    <div className="p-4 flex flex-col gap-2">
      <p className="text-xl font-bold text-[#aaa] italic">My Layouts</p>

      {busy && (
        <div className="p-2 rounded-xl bg-white text-black">
          <progress />
        </div>
      )}

      {!busy && layouts && layouts.length === 0 && (
        <div className="p-2 rounded-xl bg-white text-black">
          You haven&apos;t uploaded any layouts yet. Build one in the Field Editor and click &quot;Upload to Game.&quot;
        </div>
      )}

      {!busy && layouts && layouts.length > 0 && (
        <div className="p-2 rounded-xl bg-white text-black">
          <table className="leaderboard">
            <thead>
              <tr>
                <th>Name</th>
                <th>Uploaded</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {layouts.map((layout) => (
                <tr key={layout.itemId}>
                  <th>{layout.name}</th>
                  <td>{formatDate(layout.createdDate)}</td>
                  <td>
                    <button
                      onClick={() => handleEdit(layout)}
                      className="px-2 py-1 rounded-lg bg-[#0095da] font-bold text-white cursor-pointer"
                    >
                      Edit
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => handleDelete(layout)}
                      disabled={deletingId === layout.itemId}
                      className="px-2 py-1 rounded-lg bg-[#823] font-bold text-white cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {deletingId === layout.itemId ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
